import * as React from "react";
import { Button, Checkbox, Dialog } from "@/components/ui";
import { DesignPackageDetail } from "@/services/bedrock/dtos/DesignPackage";

interface UnpublishDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  pkg: DesignPackageDetail;
  onConfirm(opts: { deleteScheduledPosts: boolean }): Promise<void>;
}

/** Shared by the editor and the prompt page so both unpublish the same way. */
export const UnpublishDialog: React.FC<UnpublishDialogProps> = ({ open, onOpenChange, pkg, onConfirm }) => {
  const [deleteScheduledPosts, setDeleteScheduledPosts] = React.useState(false);
  const [pending, setPending] = React.useState(false);

  React.useEffect(() => {
    if (open) setDeleteScheduledPosts(false);
  }, [open]);

  const confirm = async () => {
    setPending(true);
    try {
      await onConfirm({ deleteScheduledPosts });
      onOpenChange(false);
    } catch {
      // The caller reports the error; the dialog stays open to try again.
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="sm">
      <Dialog.Header>
        <Dialog.Title className="text-lg font-bold font-sans">Unpublish this design?</Dialog.Title>
        <Dialog.Description className="text-sm text-slate-500">
          Players will no longer see it in their library.
        </Dialog.Description>
      </Dialog.Header>
      {pkg.scheduledPostsCount > 0 && (
        <Dialog.Content className="flex flex-col gap-1 py-2">
          <Checkbox
            checked={deleteScheduledPosts}
            onCheckedChange={checked => setDeleteScheduledPosts(checked === true)}
            label={`Also remove the ${pkg.scheduledPostsCount} scheduled posts that use it`}
          />
          <p className="pl-6 text-xs text-slate-500">Posts already published aren't affected.</p>
        </Dialog.Content>
      )}
      <Dialog.Footer>
        <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button variant="outline" loading={pending} onClick={() => void confirm()}>
          Unpublish
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
};
